import { useState, useEffect, useRef, useCallback } from "react";

export default function AllocBarCanvas({ levels, theme, initialLevel = "all", onLevelChange, showVar = true }) {
  const canvasRef    = useRef(null);
  const rowRectsRef  = useRef([]);
  const [currentLevel, setCurrentLevel] = useState(initialLevel);

  const changeLevel = useCallback((lvl) => {
    setCurrentLevel(lvl);
    onLevelChange?.(lvl);
  }, [onLevelChange]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !levels) return;
    const d = levels[currentLevel];
    if (!d) return;

    const isLight    = theme === "light";
    const textColor  = (a) => isLight ? `rgba(0,0,0,${a})`         : `rgba(255,255,255,${a})`;
    const gridColor  = ()  => isLight ? "rgba(0,0,0,0.06)"          : "rgba(255,255,255,0.04)";
    const trackColor = ()  => isLight ? "rgba(0,0,0,0.07)"          : "rgba(255,255,255,0.05)";
    const pctLbl     = ()  => isLight ? "rgba(0,0,0,0.85)"          : "rgba(255,255,255,0.92)";
    const axisLbl    = ()  => isLight ? "rgba(0,0,0,0.35)"          : "rgba(255,255,255,0.3)";

    const ctx  = canvas.getContext("2d");
    const rows = d.rows;
    const dpr  = window.devicePixelRatio || 1;
    const W    = canvas.parentElement ? canvas.parentElement.clientWidth : 300;
    if (W <= 0) return;
    const barH = 26, gap = 18, labelW = 90, padTop = 8, axisH = 24, padBot = 4;
    const H    = padTop + rows.length * (barH + gap) - gap + axisH + padBot;

    canvas.width        = W * dpr;
    canvas.height       = H * dpr;
    canvas.style.width  = W + "px";
    canvas.style.height = H + "px";
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, W, H);

    const trackW = W - labelW - 8;
    const maxVal = Math.max(...rows.map(r => r.mv + r.var));
    rowRectsRef.current = [];

    rows.forEach((row, i) => {
      const y   = padTop + i * (barH + gap);
      const cy  = y + barH / 2;
      const mvPx  = (row.mv  / maxVal) * trackW;
      const varPx = (row.var / maxVal) * trackW;
      const x0 = labelW;

      // Row label
      ctx.font = "400 12px 'Outfit', sans-serif";
      ctx.fillStyle = textColor(0.92);
      ctx.textAlign = "right";
      ctx.textBaseline = "middle";
      ctx.fillText(row.label, labelW - 6, cy);

      // Blue underline on drillable labels
      if (row.child) {
        const tw = ctx.measureText(row.label).width;
        ctx.strokeStyle = "rgba(79,111,247,0.55)";
        ctx.lineWidth = 0.75;
        ctx.beginPath();
        ctx.moveTo(labelW - 6 - tw, cy + 8);
        ctx.lineTo(labelW - 6, cy + 8);
        ctx.stroke();
      }

      // Track background
      ctx.fillStyle = trackColor();
      ctx.beginPath();
      ctx.roundRect(x0, y, trackW, barH, 3);
      ctx.fill();

      // Allocation segment (dark navy)
      ctx.fillStyle = "#2d3a6b";
      ctx.beginPath();
      ctx.roundRect(x0, y, mvPx, barH, varPx > 0 ? [3, 0, 0, 3] : 3);
      ctx.fill();

      // Risk contribution segment (teal) — only when showVar is enabled
      if (showVar && varPx > 0) {
        ctx.fillStyle = "#5a8a78";
        ctx.beginPath();
        ctx.roundRect(x0 + mvPx, y, varPx, barH, [0, 3, 3, 0]);
        ctx.fill();
      }

      // In-bar % labels — inside if wide enough, offset outside if not
      ctx.font = "500 11px 'DM Mono', monospace";
      ctx.textBaseline = "middle";
      if (mvPx >= 38) {
        ctx.fillStyle = pctLbl();
        ctx.textAlign = "center";
        ctx.fillText(row.mv.toFixed(0) + "%", x0 + mvPx / 2, cy);
      } else {
        ctx.fillStyle = "rgba(180,200,255,0.75)";
        ctx.textAlign = "left";
        ctx.fillText(row.mv.toFixed(0) + "%", x0 + mvPx + 3, cy - 7);
      }
      if (showVar) {
        if (varPx >= 38) {
          ctx.fillStyle = pctLbl();
          ctx.textAlign = "center";
          ctx.fillText(row.var.toFixed(0) + "%", x0 + mvPx + varPx / 2, cy);
        } else if (varPx > 0) {
          ctx.fillStyle = "rgba(140,210,185,0.85)";
          ctx.textAlign = "left";
          ctx.fillText(row.var.toFixed(0) + "%", x0 + mvPx + varPx + 3, cy + 7);
        }
      }

      rowRectsRef.current.push({ y, h: barH, child: row.child });
    });

    // X-axis
    const axisY = H - axisH + 6;
    ctx.strokeStyle = gridColor();
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(labelW, axisY);
    ctx.lineTo(labelW + trackW, axisY);
    ctx.stroke();

    ctx.font = "400 9px 'DM Mono', monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    for (let t = 0; t <= 5; t++) {
      const pct = (t / 5) * maxVal;
      const px  = labelW + (t / 5) * trackW;
      ctx.strokeStyle = axisLbl();
      ctx.lineWidth = 0.75;
      ctx.beginPath();
      ctx.moveTo(px, axisY);
      ctx.lineTo(px, axisY + 3);
      ctx.stroke();
      ctx.fillStyle = axisLbl();
      ctx.fillText(Math.round(pct) + "%", px, axisY + 5);
    }
  }, [currentLevel, levels, theme]);

  useEffect(() => { draw(); }, [draw]);

  // Redraw on container resize
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas?.parentElement) return;
    const ro = new ResizeObserver(() => draw());
    ro.observe(canvas.parentElement);
    return () => ro.disconnect();
  }, [draw]);

  const handleClick = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const my = e.clientY - rect.top;
    for (const r of rowRectsRef.current) {
      if (my >= r.y && my <= r.y + r.h && r.child) { changeLevel(r.child); return; }
    }
  };

  const handleMouseMove = (e) => {
    const canvas = canvasRef.current;
    const rect   = canvas.getBoundingClientRect();
    const my     = e.clientY - rect.top;
    canvas.style.cursor = rowRectsRef.current.some(r => my >= r.y && my <= r.y + r.h && r.child)
      ? "pointer" : "default";
  };

  const node = levels?.[currentLevel] || { subtitle: "", parent: null, parentLabel: null, rows: [] };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
      {/* Subtitle + breadcrumb */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.52rem", color: "var(--dash-muted2)" }}>
          {node.subtitle}
        </div>
        <div style={{ fontSize: "0.58rem", color: "var(--dash-muted2)", display: "flex", gap: "0.2rem", alignItems: "center" }}>
          {currentLevel !== "all" ? (
            <>
              <span style={{ color: "#4f6ef7", cursor: "pointer" }} onClick={() => changeLevel("all")}>All</span>
              {node.parent && node.parent !== "all" && (
                <>
                  {" › "}
                  <span style={{ color: "#4f6ef7", cursor: "pointer" }} onClick={() => changeLevel(node.parent)}>
                    {node.parentLabel}
                  </span>
                </>
              )}
              {" › "}
              <span>{node.subtitle.split("·")[0].trim()}</span>
            </>
          ) : (
            <span>All</span>
          )}
        </div>
      </div>

      {/* Canvas */}
      <canvas
        ref={canvasRef}
        onClick={handleClick}
        onMouseMove={handleMouseMove}
        style={{ display: "block", width: "100%" }}
      />

      {/* Legend */}
      <div style={{ display: "flex", justifyContent: "center", gap: "1.5rem", fontSize: "0.6rem", color: "var(--dash-muted2)", marginTop: "0.1rem" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: "#2d3a6b", display: "inline-block" }} />
          Allocation
        </span>
        {showVar && (
          <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: "#5a8a78", display: "inline-block" }} />
            Risk Contribution
          </span>
        )}
      </div>
    </div>
  );
}
